/**
 * The sidebar's folders, in the pane beside it.
 *
 * Filing something in the tree used to change nothing on the screen next to it: a
 * document moved into "Aldermead" was still in the Documents list at the top level,
 * and there was no way to look inside the folder except by opening it in the tree. The
 * list screens now show one folder at a time — the way back up, the folders inside,
 * and the things filed there — with the folder in the address, so it survives a
 * reload and the back button.
 */
import { Icon, count } from "../ui/index.js";
import type { TreeFolder } from "./tree.js";

/** A list screen showing one of its folders. */
export function folderHref(base: string, folderId: string | null): string {
  return folderId ? `${base}?folder=${encodeURIComponent(folderId)}` : base;
}

/** Where in the folders the list is, with every level above it a link. Nothing at the top. */
export function FolderCrumbs({ base, label, crumbs }: { base: string; label: string; crumbs: Array<{ id: string; name: string }> }) {
  if (!crumbs.length) return null;
  return (
    <nav className="crumbs" aria-label="Folder">
      <a href={base}>{label}</a>
      {crumbs.map((crumb, i) => (
        <span key={crumb.id} className="crumbs__step">
          <Icon name="chevron" className="crumbs__sep" />
          {i === crumbs.length - 1 ? <strong aria-current="page">{crumb.name}</strong> : <a href={folderHref(base, crumb.id)}>{crumb.name}</a>}
        </span>
      ))}
    </nav>
  );
}

/** A folder as the first rows of a table. The whole row opens it, as a file browser's does. */
export function FolderTableRow({ base, folder, span, noun }: { base: string; folder: TreeFolder; span: number; noun: string }) {
  return (
    <tr className="table__folder">
      <td colSpan={span}>
        <a className="folder-link" href={folderHref(base, folder.id)}>
          <Icon name="folder" className="folder-link__icon" />
          <strong>{folder.name}</strong>
          <span className="small muted">
            {count(folder.total)} {folder.total === 1 ? noun : `${noun}s`}
          </span>
        </a>
      </td>
    </tr>
  );
}

/** A folder among a grid of tiles. */
export function FolderTile({ base, folder, noun }: { base: string; folder: TreeFolder; noun: string }) {
  return (
    <a className="tile tile--folder" href={folderHref(base, folder.id)}>
      <Icon name="folder" className="folder-link__icon" />
      <span className="stack" style={{ gap: 3 }}>
        <strong>{folder.name}</strong>
        <span className="small muted">
          {count(folder.total)} {folder.total === 1 ? noun : `${noun}s`}
        </span>
      </span>
    </a>
  );
}
